import { Suspense, lazy } from "react";
import FXBackdrop from "../components/FXBackdrop";
import PageTransition from "../components/PageTransition";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Heart, Building2, Shield, TrendingUp, Users, CalendarCheck } from "lucide-react";
import { NavLink } from "react-router-dom";

const Accent3D = lazy(() => import("../components/Accent3D"));

export default function Candidates() {
  return (
    <PageTransition>
      <FXBackdrop>
        {/* HERO — light */}
        <section className="containerX pt-10 md:pt-16 pb-10 relative">
          <div className="float-shape bg-purple-400 h-12 w-12 top-6 right-12 hidden lg:block" style={{ animationDelay: "0.5s" }} />
          <div className="float-shape-slow bg-violet-300 h-8 w-8 bottom-4 left-8 hidden lg:block" style={{ animationDelay: "2s" }} />

          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <div className="chip">
                <span className="h-2 w-2 rounded-full" style={{ background: "rgb(139,92,246)" }} /> For Engineers
              </div>
              <h1 className="h1 mt-5">Get matched to roles that <span className="text-black">actually fit.</span></h1>
              <p className="p mt-4 max-w-2xl">
                No ghost jobs, no endless forms. We focus on high-signal profiles and real opportunities.
              </p>
              <div className="mt-7">
                <NavLink to="/signup/engineer" className="btn btnPrimary pulse-glow">Join the Network <ArrowRight size={16} /></NavLink>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                <Heart size={14} className="text-violet-500 flex-shrink-0" />
                <span>Full benefits package launching Q4 2026: health, pension and ownership stake</span>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="hidden lg:flex items-center justify-center"
            >
              <Suspense fallback={<div className="h-[280px]" />}>
                <Accent3D variant="candidate" className="w-full h-[280px]" />
              </Suspense>
            </motion.div>
          </div>
        </section>

        {/* BENEFITS PROGRAM — dark */}
        <section className="dark-section py-12 md:py-16 relative overflow-hidden">
          <div className="float-shape bg-violet-500 h-20 w-20 -top-8 -left-8" style={{ animationDelay: "1s" }} />
          <div className="containerX">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="chip"><Shield size={14} /> Benefits Program</div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "rgba(139,92,246,0.2)", border: "1px solid rgba(139,92,246,0.35)", color: "rgba(196,181,253,1)" }}>
                <CalendarCheck size={12} /> Launching Q4 2026
              </span>
            </div>

            <h2 className="h2 mt-4">The infrastructure that makes hirrd your long-term home.</h2>
            <p className="p mt-3 max-w-2xl">
              No other agency in this space offers this. We invest in your health, financial future, and career because the best engineers deserve more than a placement fee.
            </p>

            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                [Heart, "Health Insurance", "Group health coverage for you and your family, a benefit almost no remote engineer in South Asia receives through an employer."],
                [TrendingUp, "Pension & Investment", "Pension benefits and investment fund access. Build long-term financial security, not just a monthly salary."],
                [Shield, "Ownership Stake", "After reaching an income milestone with hirrd, earn an ownership stake in the company. When we grow, you grow."],
                [Sparkles, "Career Growth", "Training budgets, certifications, and a monthly one-on-one with a dedicated hirrd talent advisor."],
              ].map(([Icon, t, d], i) => (
                <motion.div
                  key={t} className="glass-dark p-6 group"
                  whileHover={{ y: -7, scale: 1.04, boxShadow: "0 20px 50px rgba(139,92,246,0.18)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <motion.div
                    className="inline-block"
                    whileHover={{ rotate: 12, scale: 1.15 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Icon size={18} className="text-violet-400" />
                  </motion.div>
                  <div className="font-semibold mt-3">{t}</div>
                  <div className="p mt-2 text-sm">{d}</div>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="mt-8 rounded-2xl p-6"
              style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.25)" }}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-white/75 text-sm leading-relaxed max-w-3xl">
                We're not building features. We're building the infrastructure that makes the best South Asian engineers choose hirrd over Upwork, Toptal, or any other option and stay for years.
              </p>
            </motion.div>
          </div>
        </section>

        {/* COMMUNITY — light */}
        <section className="containerX py-12 md:py-16 relative">
          <div className="float-shape bg-violet-400 h-10 w-10 top-2 right-20 hidden lg:block" style={{ animationDelay: "1.5s" }} />
          <div className="chip"><Users size={14} /> Community</div>
          <h2 className="h2 mt-4">You're not alone. You're part of a <span className="text-black">nation.</span></h2>
          <p className="p mt-3 max-w-2xl">
            Connect with other engineers, share knowledge, and grow together. No more isolation in remote work.
          </p>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {[
              ["Talk & Communicate", "Private community channels for engineers to connect, share, and support each other."],
              ["Training & Courses", "Access curated training, courses, and workshops to keep your skills sharp."],
              ["Events & Networking", "Regular meetups, tech talks, and networking events with peers and industry leaders."],
            ].map(([t, d], i) => (
              <motion.div
                key={t} className="glass p-6"
                whileHover={{ y: -7, scale: 1.03, boxShadow: "0 20px 50px rgba(139,92,246,0.12)" }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 300, damping: 22 }}
              >
                <div className="font-semibold text-lg">{t}</div>
                <div className="p mt-2">{d}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CANDIDATE PORTAL — dark */}
        <section className="dark-section py-12 md:py-16 relative overflow-hidden">
          <div className="float-shape-slow bg-purple-500 h-28 w-28 -bottom-10 -right-10" style={{ animationDelay: "0.8s" }} />
          <div className="containerX">
            <div className="chip"><Building2 size={14} /> Candidate Portal</div>
            <h2 className="h2 mt-4">Your hirrd home base.</h2>
            <p className="p mt-3 max-w-2xl">
              Every engineer placed through hirrd gets private access to their own portal. Track your placement, reach your advisor, and manage your career. All in one place.
            </p>

            <div className="mt-8 grid md:grid-cols-2 gap-4">
              {[
                [Building2, "My Placement", "Your live placement details, contract terms, and milestones. Always know your current status and what comes next."],
                [Users, "Advisor Access", "Direct line to your dedicated hirrd talent advisor. Message, schedule a call, or get help any time."],
                [TrendingUp, "Career Tracker", "Set growth goals, track your progress, and get personalised recommendations for your next move."],
                [Shield, "My Documents", "Contracts, offer letters, and payroll records. Secure, organised and always accessible."],
              ].map(([Icon, t, d], i) => (
                <motion.div
                  key={t} className="glass-dark p-6"
                  whileHover={{ y: -6, scale: 1.03, boxShadow: "0 18px 44px rgba(139,92,246,0.15)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <Icon size={18} className="text-violet-400" />
                  <div className="font-semibold mt-3">{t}</div>
                  <div className="p mt-2">{d}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* JOURNEY — dark */}
        <section className="dark-section py-12 md:py-16">
          <div className="containerX">
            <div className="chip">Candidate journey</div>
            <h2 className="h2 mt-4">A clean 4-step process</h2>

            <div className="mt-8 grid md:grid-cols-2 gap-4">
              {[
                ["01", "Profile review", "We review your profile and experience for fit."],
                ["02", "Quick screening", "Short call to understand goals and expectations."],
                ["03", "Matching", "We propose roles that match your skills + preferences."],
                ["04", "Interview support", "Prep, feedback, and offer negotiation help."],
              ].map(([n, t, d], i) => (
                <motion.div
                  key={n} className="glass-dark p-6 group"
                  whileHover={{ y: -6, scale: 1.02, boxShadow: "0 18px 44px rgba(139,92,246,0.15)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <div className="flex items-center gap-3">
                    <motion.div
                      className="h-10 w-10 rounded-2xl flex items-center justify-center font-semibold"
                      style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)" }}
                      whileHover={{ scale: 1.2, rotate: 6 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      {n}
                    </motion.div>
                    <div className="font-semibold">{t}</div>
                  </div>
                  <div className="p mt-2">{d}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SIGNUP CTA — light */}
        <section className="containerX pb-16 pt-12">
          <motion.div
            className="glass p-8 md:p-10 text-center"
            whileHover={{ scale: 1.015, boxShadow: "0 28px 70px rgba(139,92,246,0.14)" }}
            transition={{ duration: 0.35 }}
          >
            <div className="h2">Ready to join the <span className="text-black">network?</span></div>
            <p className="p mt-2 max-w-2xl mx-auto">
              Create your profile and we'll match you with roles that fit your skills, experience, and preferences.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <NavLink to="/signup/engineer" className="btn btnPrimary px-7 pulse-glow">
                Join hirrd <ArrowRight size={16} />
              </NavLink>
            </div>
          </motion.div>
        </section>
      </FXBackdrop>
    </PageTransition>
  );
}
